import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Grid, Paper, makeStyles } from '@material-ui/core';
import { useFormik } from 'formik';
import { DadosDelivery } from 'storage/delivery/models';
import { DeliveryDadosFormEntregaValidation } from './delivery-dados-entrega-validation';
import { DeliveryDadosInstancia } from './delivery-dados-instancia';
import { TextFieldSaurus } from 'components/inputs/text-field-saurus';
import { useConsultaCep } from 'webservice';
import { BuscarCepResultado } from 'webservice/consulta-cep/models';
import { UfSelect } from 'components';

const useStyles = makeStyles((theme) => ({
  form: {
    marginTop: theme.spacing(4),
  },
}));

export interface DeliveryDadosEntregaFormProps {
  dadosDelivery: DadosDelivery | undefined;
  onSubmit: () => Promise<void>
}

export const DeliveryDadosEntregaForm = React.forwardRef<
  DeliveryDadosInstancia,
  DeliveryDadosEntregaFormProps
>(({ dadosDelivery, onSubmit }: DeliveryDadosEntregaFormProps, ref) => {
  const classes = useStyles();
  const inputNumeroRef = useRef<HTMLDivElement | null>(null);
  const [, setAtt] = useState<boolean>(false)
  const { consultarCep } = useConsultaCep()

  useEffect(() => {
    setAtt(prev => !prev)
  }, [dadosDelivery])

  React.useImperativeHandle(ref, () => ({
    submit: triggerSubmit,
  }));
  const {
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    setFieldValue,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      lgr: dadosDelivery?.lgr ?? '',
      nro: dadosDelivery?.nro ?? '',
      bairro: dadosDelivery?.bairro ?? '',
      cep: dadosDelivery?.cep ?? '',
      cpl: dadosDelivery?.cpl ?? '',
      uf: dadosDelivery?.uf ?? '',
      municipio: dadosDelivery?.municipio ?? '',
      referencia: dadosDelivery?.referencia ?? '',
    },
    validationSchema: DeliveryDadosFormEntregaValidation,
    onSubmit: () => { },
  });

  const quandoEncontrarUmCepExecutar = useCallback(
    (dados: any) => {
      setFieldValue('lgr', dados.consultaCEP.Logradouro, true);
      setFieldValue('bairro', dados.consultaCEP.Bairro, true);
      setFieldValue('municipio', dados.consultaCEP.Municipio, true);
      setFieldValue('uf', dados.consultaCEP.UF, true);
      inputNumeroRef?.current?.focus();
    },
    [setFieldValue, inputNumeroRef],
  );

  const triggerSubmit = () => {
    handleSubmit();
    if (Object.entries(errors).length > 0 || values.lgr === undefined) {
      return { sucesso: false, data: undefined, erros: errors };
    }
    return { sucesso: true, data: values, erros: undefined };
  };


  return (
    <Paper className={classes.form} elevation={0}>
      <Grid spacing={2} container>
        <Grid item xs={12}>
          <TextFieldSaurus
            label="CEP"
            tipo="CEP"
            name="cep"
            onChange={handleChange}
            searchable
            onBlur={handleBlur}
            value={values.cep}
            onSearch={async () => {
              if (!values.cep) return
              const result = await consultarCep((values.cep ?? '') as string);
              if (result?.sucesso) {
                quandoEncontrarUmCepExecutar(result?.dados as BuscarCepResultado);
              }
            }}
            error={Boolean(errors.cep && touched.cep)}
            helperText={errors.cep && touched.cep ? errors.cep : undefined}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={8} sm={8}>
          <TextFieldSaurus
            tipo="TEXTO"
            label="Logradouro"
            placeholder="Logradouro"
            name="lgr"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.lgr}
            error={Boolean(touched.lgr && errors.lgr)}
            helperText={touched.lgr && errors.lgr ? errors.lgr : undefined}
            InputLabelProps={{ shrink: true }}
            allowSubmit
          />
        </Grid>
        <Grid item xs={4} sm={4}>
          <TextFieldSaurus
            inputRef={inputNumeroRef}
            label="Número"
            name="nro"
            placeholder="Número"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.nro}
            error={Boolean(touched.nro && errors.nro)}
            helperText={touched.nro && errors.nro ? errors.nro : undefined}
            InputLabelProps={{ shrink: true }}
            allowSubmit
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextFieldSaurus
            label="Complemento"
            name="cpl"
            placeholder="Complemento"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.cpl}
            error={Boolean(touched.cpl && errors.cpl)}
            helperText={touched.cpl && errors.cpl ? errors.cpl : undefined}
            InputLabelProps={{ shrink: true }}
            allowSubmit
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextFieldSaurus
            label="Bairro"
            name="bairro"
            placeholder="Bairro"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.bairro}
            error={Boolean(touched.bairro && errors.bairro)}
            helperText={touched.bairro && errors.bairro ? errors.bairro : undefined}
            InputLabelProps={{ shrink: true }}
            allowSubmit
          />
        </Grid>
        <Grid item xs={8} sm={6}>
          <TextFieldSaurus
            label="Município"
            name="municipio"
            placeholder="Município"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.municipio}
            error={Boolean(touched.municipio && errors.municipio)}
            helperText={touched.municipio && errors.municipio ? errors.municipio : undefined}
            InputLabelProps={{ shrink: true }}
            allowSubmit
          />
        </Grid>
        <Grid item xs={4} sm={6}>
          <UfSelect
            {...{
              label: 'UF',
              placeholder: 'UF',
              name: 'uf',
              error: Boolean(touched.uf && errors.uf),
              helperText: Boolean(touched.uf && errors.uf) ? errors.uf : undefined,
            }}
            ufValue={values.uf ? values.uf : ''}
            onInputChange={(event, newValue) => {
              if (event) {
                setFieldValue('uf', newValue, true);
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <TextFieldSaurus
            allowSubmit
            label="Referência"
            name="referencia"
            placeholder="Referência"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.referencia}
            error={Boolean(touched.referencia && errors.referencia)}
            helperText={touched.referencia && errors.referencia ? errors.referencia : undefined}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
});
